import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ordersApi } from '../services/api/orders.api';
import { CheckCircleIcon, ClockIcon, XCircleIcon, ArrowRightIcon, CreditCardIcon } from '@heroicons/react/outline';
import { BASE_URL } from '../config/axios';

const RiyalIcon = () => (
    <span style={{ fontFamily: 'saudi_riyal', fontSize: '1.1em' }}>&#xE900;</span>
);

const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  if (path.startsWith('/storage')) return `${BASE_URL}${path}`;
  return `${BASE_URL}/storage/${path}`;
};

const formatDate = (dateString) => {
    const d = new Date(dateString);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
};

const statusConfig = {
    paid: { icon: CheckCircleIcon, color: 'text-green-500', bg: 'bg-green-100 dark:bg-green-900/30', label: 'تم الدفع' },
    pending: { icon: ClockIcon, color: 'text-amber-500', bg: 'bg-amber-100 dark:bg-amber-900/30', label: 'في انتظار الدفع' },
    overdue: { icon: XCircleIcon, color: 'text-red-500', bg: 'bg-red-100 dark:bg-red-900/30', label: 'متأخر' },
};

export default function InstallmentDetails() {
    const { orderId } = useParams();
    const navigate = useNavigate();
    const [plan, setPlan] = useState(null);
    const [loading, setLoading] = useState(true);
    const [payLoading, setPayLoading] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const data = await ordersApi.getInstallmentDetails(orderId);
                setPlan(data);
            } catch (err) {
                navigate('/profile?tab=wallet');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [orderId, navigate]);

    if (loading) {
        return (
            <div className="min-h-screen pt-16 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    if (!plan) return null;

    const course = plan.course;
    const paidCount = plan.schedule.filter(s => s.status === 'paid').length;
    const totalInstallments = plan.installment_count;
    const today = new Date();
    const overdueSchedules = plan.schedule.filter(s => s.status === 'pending' && new Date(s.due_date) < today);
    const pendingSchedules = plan.schedule.filter(s => s.status === 'pending');
    const upcomingSchedules = pendingSchedules
        .filter(s => new Date(s.due_date) >= today)
        .sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
    const nextSchedule = upcomingSchedules[0];
    const overdueTotal = overdueSchedules.reduce((sum, s) => sum + s.amount, 0);
    const remainingTotal = pendingSchedules.reduce((sum, s) => sum + s.amount, 0);

    const handlePayOverdue = async () => {
        setPayLoading(true);
        try {
            const data = await ordersApi.payInstallment(plan.id, 'overdue');
            if (data.payment_url) {
                window.location.href = data.payment_url;
            }
        } catch (err) {
            const msg = err.response?.data?.message || 'حدث خطأ أثناء الدفع';
            alert(msg);
        } finally {
            setPayLoading(false);
        }
    };

    const handlePayNext = async () => {
        setPayLoading(true);
        try {
            const data = await ordersApi.payInstallment(plan.id, 'next');
            if (data.payment_url) {
                window.location.href = data.payment_url;
            }
        } catch (err) {
            const msg = err.response?.data?.message || 'حدث خطأ أثناء الدفع';
            alert(msg);
        } finally {
            setPayLoading(false);
        }
    };

    const handlePayAll = async () => {
        setPayLoading(true);
        try {
            const data = await ordersApi.payInstallment(plan.id, 'all');
            if (data.payment_url) {
                window.location.href = data.payment_url;
            }
        } catch (err) {
            const msg = err.response?.data?.message || 'حدث خطأ أثناء الدفع';
            alert(msg);
        } finally {
            setPayLoading(false);
        }
    };

    return (
        <div className="min-h-screen pt-16">
            <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
                <button
                    onClick={() => navigate('/profile?tab=wallet')}
                    className="flex items-center text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 mb-6"
                >
                    <ArrowRightIcon className="h-4 w-4 ml-1" />
                    العودة إلى المحفظة
                </button>

                {/* Header */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
                    <div className="flex items-start gap-4">
                        {getImageUrl(course?.cover_image) && (
                            <img
                                src={getImageUrl(course.cover_image)}
                                alt={course.name}
                                className="h-16 w-16 rounded-xl object-cover"
                            />
                        )}
                        <div className="flex-1 min-w-0">
                            <h1 className="text-xl font-bold text-gray-900 dark:text-white truncate">
                                {course?.name || 'دورة تدريبية'}
                            </h1>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                خطة تقسيط · {plan.installment_count} دفعات
                            </p>
                        </div>
                        <div className="text-left">
                            <div className="text-2xl font-bold text-gray-900 dark:text-white">
                                {plan.total_amount} <RiyalIcon />
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                إجمالي المبلغ
                            </div>
                        </div>
                    </div>

                    {/* Progress bar */}
                    <div className="mt-6">
                        <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
                            <span>تم سداد {paidCount} من {totalInstallments} دفعات</span>
                            <span>{Math.round((paidCount / totalInstallments) * 100)}%</span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div
                                className="bg-primary-600 h-2 rounded-full transition-all duration-500"
                                style={{ width: `${(paidCount / totalInstallments) * 100}%` }}
                            />
                        </div>
                    </div>
                </div>

                {/* Timeline */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">جدول الدفعات</h2>
                    <div className="relative">
                        {plan.schedule.map((item, idx) => {
                            const config = statusConfig[item.status] || statusConfig.pending;
                            const Icon = config.icon;
                            const isLast = idx === plan.schedule.length - 1;
                            const isFirst = idx === 0;

                            return (
                                <div key={item.id} className="relative flex gap-4 pb-8 last:pb-0">
                                    {/* خط الربط العمودي */}
                                    {!isLast && (
                                        <div className="absolute right-[19px] top-10 bottom-0 w-0.5 bg-gray-200 dark:bg-gray-600" />
                                    )}

                                    {/* الدائرة */}
                                    <div className={`relative flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${config.bg} ${item.status === 'paid' ? 'ring-2 ring-green-300 dark:ring-green-700' : ''}`}>
                                        <Icon className={`h-5 w-5 ${config.color}`} />
                                    </div>

                                    {/* المحتوى */}
                                    <div className={`flex-1 min-w-0 ${item.status === 'paid' ? 'opacity-75' : ''}`}>
                                        <div className="flex items-center justify-between">
                                            <h3 className={`text-base font-medium ${item.status === 'pending' ? 'text-gray-900 dark:text-white font-semibold' : 'text-gray-700 dark:text-gray-300'}`}>
                                                {isFirst ? 'الدفعة الأولى' : `الدفعة ${idx + 1}`}
                                            </h3>
                                            <span className="text-base font-bold text-gray-900 dark:text-white">
                                                {item.amount} <RiyalIcon />
                                            </span>
                                        </div>
                                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                                            {item.paid_date ? `تم الدفع في ${formatDate(item.paid_date)}` : `تاريخ الاستحقاق: ${formatDate(item.due_date)}`}
                                        </p>
                                        <span className={`inline-block mt-1 text-xs font-medium px-2 py-0.5 rounded-full ${config.color.replace('text-', 'bg-').replace('500', '100')} ${config.color} dark:${config.bg}`}>
                                            {config.label}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Pay buttons */}
                <div className="mt-6 space-y-3">
                    {overdueSchedules.length > 0 && (
                        <button
                            onClick={handlePayOverdue}
                            disabled={payLoading}
                            className="w-full px-6 py-3 text-base font-medium text-white bg-primary-600 rounded-xl hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                        >
                            <CreditCardIcon className="h-5 w-5" />
                            {payLoading ? 'جاري الدفع...' : <>سدد القسط المستحق ({overdueTotal} <RiyalIcon />)</>}
                        </button>
                    )}
                    {!overdueSchedules.length && nextSchedule && (
                        <button
                            onClick={handlePayNext}
                            disabled={payLoading}
                            className="w-full px-6 py-3 text-base font-medium text-white bg-primary-700 rounded-xl hover:bg-primary-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                        >
                            <CreditCardIcon className="h-5 w-5" />
                            {payLoading ? 'جاري الدفع...' : <>ادفع الآن ({nextSchedule.amount} <RiyalIcon />)</>}
                        </button>
                    )}
                    {pendingSchedules.length > 0 && (
                        <button
                            onClick={handlePayAll}
                            disabled={payLoading}
                            className="w-full px-6 py-3 text-base font-medium text-primary-600 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-xl hover:bg-primary-100 dark:hover:bg-primary-900/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                        >
                            {payLoading ? 'جاري الدفع...' : <>سدد الكل ({remainingTotal} <RiyalIcon />)</>}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
